import React from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Server } from '../server/server';
import { AppConfig } from './AppConfig';
import SitePage from './pages/SitePage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import FriendsPage from './pages/FriendsPage';
import ProfilePage from './pages/ProfilePage';
import MediaPage from './pages/MediaPage';
import StatsPage from './pages/StatsPage';
import ChatPage from './pages/ChatPage';
import NotFoundPage from './pages/NotFoundPage';
import ActivityPage from './pages/ActivityPage';
import ActivityPostPage from './pages/ActivityPostPage';
import BlogPage from './pages/BlogPage';
import BlogsPage from './pages/BlogsPage';
import EditBlogPage from './pages/EditBlogPage';
import ForumContentPage from './pages/ForumContentPage';
import ForumPage from './pages/ForumPage';
import ForumTopicPage from './pages/ForumTopicPage';
import ForumsPage from './pages/ForumsPage';
import NotificationsPage from './pages/NotificationsPage';
import CreateCommunityPage from './pages/CreateCommunityPage';
import AdminPage from './pages/AdminPage';
import AdminThemePage from './pages/AdminThemePage';
import withRouter from './util/withRouter';
import SplashPage from './pages/SplashPage';
import DashboardPage from './pages/DashboardPage';
import JoinCommunityPage from './pages/JoinCommunityPage';
import EditForumPage from './pages/EditForumPage';
import UsersPage from './pages/UsersPage';
import CommunityInvitesPage from './pages/CommunityInvitesPage';
import CommunityInvitePage from './pages/CommunityInvitePage';
import AccountPage from './pages/AccountPage';
import ChatsPage from './pages/ChatsPage';
import MaintenancePage from './pages/MaintenancePage';
import './App.css';

const ProfilePageWithRouter = withRouter(ProfilePage);
const UsersPageWithRouter = withRouter(UsersPage);
const ActivityPageWithRouter = withRouter(ActivityPage);

function RequireAuth({ children }) {
  return Server.isLoggedIn() ? children : <Navigate to="/" replace />;
}

interface AppState {
  initialized: boolean;
  maintenance: boolean;
};

class App extends React.Component<{}, AppState> {
  constructor(props) {
    super(props);
    this.state = {
      initialized: false,
      maintenance: false
    };
    
    this.onLogin = this.onLogin.bind(this);
    this.onLogout = this.onLogout.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  componentDidMount() {
    window.addEventListener('beforeinstallprompt', (e) => {
      SettingsPage.pwa = e;
    });
    window.addEventListener('beforeunload', function (e) {
      Server.network.disconnect();
    });
    Server.addEventListener('login', this.onLogin);
    Server.addEventListener('logout', this.onLogout);
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    Server.removeEventListener('login', this.onLogin);
    Server.removeEventListener('logout', this.onLogout);
    window.removeEventListener('beforeinstallprompt', (e) => {
      SettingsPage.pwa = e;
    });
    window.removeEventListener('beforeunload', function (e) {
      Server.network.disconnect();
    });
    window.removeEventListener('keydown', this.onKeyDown);
  }

  setInitialized(initialized: boolean) {
    this.setState({ initialized });
  }

  isSecretPasswordValid() {
    let sp = window.localStorage.getItem('SecretPassword');
    return (sp == 'converge');
  }

  onLogin() {
    this.forceUpdate();
  }

  onLogout() {
    if(AppConfig.community)
      this.setState({ initialized: false });
  }

  async onKeyDown(event: KeyboardEvent) {
    if (this.state.maintenance && event.altKey && event.shiftKey && event.key.toLowerCase() === 'x') 
      this.setState({ maintenance: false });
    
    // if(event.key == '`') 
      // Server.network.disconnect();
    // Server.network.abnormalDisconnect();
  }

  render() {
    const { initialized } = this.state;

    if(this.state.maintenance) 
      return (<MaintenancePage />);

    if (!initialized) 
      return (<SplashPage onInit={() => this.setInitialized(true)} />);

    return (
      <BrowserRouter>
        {AppConfig.community ? (
          <Routes>
            <Route path='/' element={<SitePage />}>
              <Route index element={<Navigate replace to="/activity" />} />
              <Route path='/activity' element={<ActivityPageWithRouter />} />
              <Route path='/activity/hashtag/:id' element={<ActivityPageWithRouter />} />
              <Route path='/activity/post/:id' element={<ActivityPostPage />} />
              <Route path='/admin' element={<AdminPage />} />
              <Route path='/admin/theme' element={<AdminThemePage />} />
              <Route path='/members' element={<UsersPageWithRouter />} />
              <Route path='/invites' element={<CommunityInvitesPage />} />
              <Route path='/invite' element={<CommunityInvitePage />} />
              <Route path='/blogs' element={<BlogsPage />} />
              <Route path='/blog/:id' element={<BlogPage />} />
              <Route path='/blog/new' element={<EditBlogPage />} />
              <Route path='/blog/edit/:id' element={<EditBlogPage />} />
              <Route path='/forums' element={<ForumsPage />} />
              <Route path='/forum/create' element={<EditForumPage />} />
              <Route path='/forum/:fid' element={<ForumPage />} />
              <Route path='/forum/:fid/edit' element={<EditForumPage />} />
              <Route path='/forum/:fid/new' element={<ForumContentPage />} />
              <Route path='/forum/:fid/:tid' element={<ForumTopicPage />} />
              <Route path='/forum/:fid/:tid/edit' element={<ForumContentPage />} />
              <Route path='/forum/:fid/:tid/new' element={<ForumContentPage />} />
              <Route path='/forum/:fid/:tid/:pid/edit' element={<ForumContentPage />} />
              <Route path='/friends' element={<FriendsPage />} />
              <Route path='/chats' element={<ChatsPage />} />
              <Route path='/chat/:id' element={<ChatPage />} />
              <Route path='/chat/group/:id' element={<ChatPage />} />
              <Route path='/notifications' element={<NotificationsPage />} />
              <Route path='/profile' element={<ProfilePageWithRouter />} />
              <Route path='/profile/:id' element={<ProfilePageWithRouter />} />
              <Route path='/profile/:id/followers' element={<UsersPageWithRouter />} />
              <Route path='/profile/:id/following' element={<UsersPageWithRouter />} />
              <Route path='/media' element={<MediaPage />} />
              <Route path='/stats' element={<StatsPage />} />
              <Route path='/account' element={<AccountPage />} />
              <Route path='/settings' element={<SettingsPage />} />
              <Route path='/subscriptions' element={<SubscriptionsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        ) : (
          <Routes>
            <Route path='/' element={<SitePage />}>
              <Route index element={<Navigate replace to="/dashboard" />} />
              <Route path='/dashboard' element={<RequireAuth><DashboardPage /></RequireAuth>} />
              <Route path='/media' element={<RequireAuth><MediaPage /></RequireAuth>} />
              <Route path='/stats' element={<RequireAuth><StatsPage /></RequireAuth>} />
              <Route path='/account' element={<RequireAuth><AccountPage /></RequireAuth>} />
              <Route path='/create' element={<RequireAuth><CreateCommunityPage /></RequireAuth>} />
              <Route path='/join' element={<RequireAuth><JoinCommunityPage /></RequireAuth>} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        )}
      </BrowserRouter>
    );
  }
}

export default App;
